// The `pictogram` structure symbol set: card 01 of mockup round 054, "CHART
// PICTOGRAMS" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// One weight of fine ink, the line of the old printed chart, and no fill: a
// crowned seat in a trench notch, a lattice mast with two radio arcs, a
// chimney with smoke curls, three kelp fronds, a tube on a mount, a cradle
// with a hull, an arched door with a keyhole, a hall with columns, and a
// coral fan. A neutral pictogram is the same line at a lower alpha.
//
// The contested tell is the chart's hatched danger line: a ring at r20 in
// the attacker's ink, with 28 ticks that lean out to r24.5. Card 01 turns
// the hatching with the clock. The atlas bakes one still cell, so the
// hatching here stands at one angle (R1104).
//
// Every offset, weight, radius and alpha below is the figure card 01 draws.

import type { MarkGraphics } from '../marks';
import {
  arcPath, cubic, disc, line, quadratic, rot, ring, stroke, TILT,
  setOf, type StructureDraw, type StructureInk, type StructureKind, type StructureSet,
} from './shared';

/** The one weight of the chart line. */
const W = 1.2;

/** The hatched danger line: the ring, the tick, and how many ticks there are. */
const DANGER_R = 20;
const DANGER_TIP = 24.5;
const DANGER_TICKS = 28;
/** How far one tick leans along the ring, in radians. */
const DANGER_LEAN = 0.28;

/**
 * The pictogram of one kind, in one ink at one alpha.
 *
 * It is `pict()` of card 01. Every curve of the card is sampled into a point
 * list here, because `MarkGraphics` carries no curve.
 */
function pict(
  g: MarkGraphics, kind: StructureKind, color: number, a: number,
): void {
  switch (kind) {
    case 'throne': {
      // The trench notch in the seabed line, the seat in it, the crown above.
      g.poly([-19, 4, -9, 4, -7, 14, 7, 14, 9, 4, 19, 4], false);
      stroke(g, color, a, W);
      g.poly([-4, 14, -4, 6, 3, 6, 3, 14], false);
      stroke(g, color, a, W);
      line(g, -4, 10, 3, 10, color, a, W);
      g.poly([-6, -3, -6, -12, -3, -7, 0, -14, 3, -7, 6, -12, 6, -3]);
      stroke(g, color, a, W);
      line(g, -6, 0, 6, 0, color, a, W);
      break;
    }
    case 'spire': {
      // A lattice mast, with two radio arcs over its head.
      g.poly([-6, 14, 0, -8, 6, 14], false);
      stroke(g, color, a, W);
      line(g, -3, 4, 3, 4, color, a, W);
      line(g, -5, 10, 5, 10, color, a, W);
      line(g, 0, -8, 0, -12, color, a, W);
      disc(g, 0, -13, 1.3, color, a);
      arcPath(g, 0, -13, 4.5, -2.5, -0.65);
      stroke(g, color, a, W);
      arcPath(g, 0, -13, 8, -2.5, -0.65);
      stroke(g, color, a, W);
      break;
    }
    case 'forge': {
      // A chimney on a hearth, with two smoke curls out of the mouth.
      g.poly([-8, 14, -5, -3, 5, -3, 8, 14], false);
      stroke(g, color, a, W);
      line(g, -12, 14, 12, 14, color, a, W);
      line(g, -6, -3, 6, -3, color, a, W);
      const curl = [0, -5];
      cubic(curl, 0, -5, 6, -8, -6, -12, 0, -15);
      cubic(curl, 0, -15, 5, -17, 3, -20, 0, -20);
      g.poly(curl, false);
      stroke(g, color, a, 1);
      const wisp = [4, -6];
      cubic(wisp, 4, -6, 9, -9, 6, -12, 8, -14);
      g.poly(wisp, false);
      stroke(g, color, a, 1);
      break;
    }
    case 'kelp': {
      // Three kelp fronds on a bed line, each with its own blades.
      line(g, -13, 14, 13, 14, color, a, W);
      const fronds = [
        [-7, 14, -14, 2, -9, -8],
        [0, 14, 5, 0, 0, -14],
        [7, 14, 14, 4, 9, -6],
      ];
      for (const [x0, y0, cx, cy, x1, y1] of fronds) {
        const pts = [x0, y0];
        quadratic(pts, x0, y0, cx, cy, x1, y1);
        g.poly(pts, false);
        stroke(g, color, a, W);
      }
      line(g, -10, 4, -14, 1, color, a, 1);
      line(g, 2, 0, 6, -3, color, a, 1);
      line(g, 10, 6, 14, 4, color, a, 1);
      line(g, -9, -3, -5, -6, color, a, 1);
      break;
    }
    case 'battery': {
      // A heavy tube on a mount, aimed up and to the right. The card tilts
      // the context; there is no transform here, so the points carry the
      // tilt and the arc's angles carry it too.
      const p = (x: number, y: number): [number, number] => rot(x, y, TILT);
      const tube = [p(-13, -3), p(10, -3), p(10, 3), p(-13, 3)];
      g.poly(tube.flat(), false);
      stroke(g, color, a, W);
      const cap = p(-13, 0);
      arcPath(g, cap[0], cap[1], 3, Math.PI / 2 + TILT, Math.PI * 1.5 + TILT);
      stroke(g, color, a, W);
      for (const x of [10, 3]) {
        const [x0, y0] = p(x, -3), [x1, y1] = p(x, 3);
        line(g, x0, y0, x1, y1, color, a, W);
      }
      g.poly([-8, 14, -3, 4, 4, 4, 9, 14], false);
      stroke(g, color, a, W);
      line(g, -13, 14, 13, 14, color, a, W);
      break;
    }
    case 'drydock': {
      // A drydock cradle with a hull in it, and two chocks.
      const cradle = [-15, -2, -13, 10];
      quadratic(cradle, -13, 10, 0, 15, 13, 10);
      cradle.push(15, -2);
      g.poly(cradle, false);
      stroke(g, color, a, W);
      const hull = [-10, -1];
      quadratic(hull, -10, -1, 0, 9, 10, -1);
      g.poly(hull);
      stroke(g, color, a, W);
      g.poly([-3, -1, -3, -6, 3, -6, 3, -1], false);
      stroke(g, color, a, W);
      line(g, -6, 10, -6, 7, color, a, W);
      line(g, 6, 10, 6, 7, color, a, W);
      break;
    }
    case 'archive': {
      // An arched door with the keyhole.
      g.moveTo(-9, 14).lineTo(-9, -4).arc(0, -4, 9, Math.PI, 0)
        .lineTo(9, 14).closePath();
      stroke(g, color, a, W);
      ring(g, 0, 1, 2.6, color, a, W);
      g.poly([-1.4, 3, -2.6, 9, 2.6, 9, 1.4, 3]);
      stroke(g, color, a, W);
      break;
    }
    case 'marshal': {
      // A hall: the pediment, four columns and two steps.
      g.poly([-14, -4, 0, -13, 14, -4]);
      stroke(g, color, a, W);
      for (const x of [-10, -3.3, 3.3, 10]) line(g, x, -4, x, 9, color, a, W);
      line(g, -14, 9, 14, 9, color, a, W);
      line(g, -16, 13, 16, 13, color, a, W);
      break;
    }
    case 'ward': {
      // A coral fan on a short stem, with a bud on every tip.
      line(g, 0, 14, 0, 5, color, a, W);
      const tips = [[-11, -5], [-5, -11], [5, -11], [11, -5]];
      for (const [x, y] of tips) line(g, 0, 5, x, y, color, a, W);
      line(g, -6, -1, -10, -11, color, a, 1);
      line(g, 6, -1, 10, -11, color, a, 1);
      for (const [x, y] of [...tips, [-10, -11], [10, -11]]) {
        disc(g, x, y, 1.3, color, a);
      }
      break;
    }
  }
}

/**
 * The hatched danger line of the chart, still.
 *
 * The ring carries one weight and the ticks another, so the two take two
 * paints. The ticks share their style, so they are one batch of 28
 * sub-paths and one stroke (`ui-map-interface.md` §10).
 */
function danger(g: MarkGraphics, color: number): void {
  ring(g, 0, 0, DANGER_R, color, 0.85, 1.1);
  for (let k = 0; k < DANGER_TICKS; k++) {
    const q = (k * Math.PI * 2) / DANGER_TICKS;
    g.moveTo(Math.cos(q) * DANGER_R, Math.sin(q) * DANGER_R)
      .lineTo(Math.cos(q + DANGER_LEAN) * DANGER_TIP,
        Math.sin(q + DANGER_LEAN) * DANGER_TIP);
  }
  stroke(g, color, 0.85, 1);
}

/** One kind of the `pictogram` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  return (g: MarkGraphics, ink: StructureInk): void => {
    pict(g, kind, ink.own, ink.held ? 0.95 : 0.65);
    if (ink.foe !== 0) danger(g, ink.foe);
  };
}

/** Card 01, all nine kinds. */
export const PICTOGRAM_SET: StructureSet = setOf(markOf);
